/**
 * 分享图生成页（独立路由 /share）
 *
 * 设计：
 *  - URL：/share?score=558&intent=邹城一中&year=2025
 *  - 用 Canvas 2D 直接绘制一张 750×1334 的竖图（适合微信朋友圈分享）
 *  - 默认模糊分数（仅显示十位以下用 "**" 替代），家长可手动取消模糊
 *  - 图片包含：方法星球品牌 + 概率结果摘要 + 二维码占位
 *  - 用户长按图片 → 保存到相册 → 发朋友圈
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { calculate } from '@/domain/probability'
import { POLICIES, PREFERENCE, NARRATIVE } from '@/store/useApp'
import type { CalcResult, SchoolName } from '@/domain/types'
import { probStrength, showProbPercent } from '@/lib/format'
import { BackfillPledge } from '@/components/BackfillPledge'
import { detectStage } from '@/domain/stage'

export function SharePage() {
  const [params] = useSearchParams()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [blurScore, setBlurScore] = useState(true) // 默认模糊
  const [imageUrl, setImageUrl] = useState<string>('')

  const score = parseInt(params.get('score') ?? '0', 10)
  const yearParam = parseInt(params.get('year') ?? '2025', 10)
  const intent = (params.get('intent') as SchoolName | null) ?? null

  const result = useMemo<CalcResult | null>(() => {
    const policy = POLICIES[yearParam] ?? POLICIES[2025]
    if (!score || !policy || policy.qualification_line === null) return null
    return calculate({
      score,
      policy,
      preference: PREFERENCE,
      narrative: NARRATIVE,
      intentSchool: intent,
      densityMethod: 'whittaker'
    })
  }, [score, yearParam, intent])

  const stageInfo = useMemo(() => {
    const policy = POLICIES[yearParam] ?? POLICIES[2025]
    return detectStage(new Date(), policy.qualification_line !== null)
  }, [yearParam])

  useEffect(() => {
    if (!result || !canvasRef.current) return
    let cancelled = false
    const canvas = canvasRef.current
    const qrImg = new Image()
    qrImg.crossOrigin = 'anonymous'
    qrImg.src = '/oa-qr.png'
    const run = (img: HTMLImageElement | null) => {
      if (cancelled) return
      drawShareImage(canvas, result, score, blurScore, img)
      setImageUrl(canvas.toDataURL('image/png', 0.92))
    }
    if (qrImg.complete && qrImg.naturalWidth > 0) {
      run(qrImg)
    } else {
      qrImg.onload = () => run(qrImg)
      qrImg.onerror = () => run(null)
    }
    return () => {
      cancelled = true
    }
  }, [result, score, blurScore])

  if (!result) {
    return (
      <div className="container-mobile py-20 text-center">
        <h1 className="text-h1 text-ink-900 mb-4">分享图参数不完整</h1>
        <Link to="/" className="btn-primary inline-block px-6 py-3">返回主页</Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-ink-900">
      <div className="bg-white border-b border-ink-100">
        <div className="container-mobile py-3 flex items-center justify-between">
          <Link to="/" className="text-ink-600 text-sm">← 返回测算</Link>
          <button
            onClick={() => setBlurScore((v) => !v)}
            className="text-brand-700 text-sm"
          >
            {blurScore ? '取消模糊（显示真实分数）' : '恢复模糊（保护隐私）'}
          </button>
        </div>
      </div>

      <div className="px-4 py-6">
        {/* 实际渲染用的 Canvas（隐藏） */}
        <canvas
          ref={canvasRef}
          width={750}
          height={1334}
          style={{ display: 'none' }}
        />

        {/* 展示给用户的图片 */}
        {imageUrl && (
          <img
            src={imageUrl}
            alt="分享图"
            className="w-full max-w-md mx-auto rounded-2xl shadow-lg"
          />
        )}

        <div className="max-w-md mx-auto mt-6 bg-white/10 text-white/90 text-sm rounded-xl px-4 py-3 leading-relaxed">
          <div className="font-medium mb-1 text-white">长按上方图片，选择"保存到相册"</div>
          再去朋友圈/家长群分享。
          {blurScore ? '分数已默认模糊处理。' : '已切换为真实分数。'}
        </div>

        {/* 录取结果回填入口（卡片自带浅色底，深色页面上仍清晰） */}
        <div className="max-w-md mx-auto mt-6">
          <BackfillPledge result={result} stageInfo={stageInfo} />
        </div>
      </div>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// Canvas 绘制核心
// ───────────────────────────────────────────────────────────────
function drawShareImage(
  canvas: HTMLCanvasElement,
  result: CalcResult,
  score: number,
  blurScore: boolean,
  qrImage: HTMLImageElement | null = null
) {
  const ctx = canvas.getContext('2d')!
  const W = 750
  const H = 1334
  const M = 60 // 边距

  // 背景渐变
  const grad = ctx.createLinearGradient(0, 0, 0, H)
  grad.addColorStop(0, '#ede9fe')
  grad.addColorStop(0.5, '#ffffff')
  grad.addColorStop(1, '#f5f3ff')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, W, H)

  // 顶部 logo + 标题（不绘 logo 图，避免跨域；用文字代替）
  ctx.fillStyle = '#1f2937'
  ctx.font = '600 28px -apple-system, "PingFang SC", sans-serif'
  ctx.fillText('方法星球', M, M + 30)
  ctx.fillStyle = '#6b7280'
  ctx.font = '400 20px -apple-system, "PingFang SC", sans-serif'
  ctx.fillText('卓越教育 · 公益项目', M, M + 60)

  // 主标题
  ctx.fillStyle = '#111827'
  ctx.font = '500 44px -apple-system, "PingFang SC", sans-serif'
  ctx.fillText('邹城中考志愿测算', M, M + 140)

  ctx.fillStyle = '#1e40af'
  ctx.font = '400 22px -apple-system, "PingFang SC", sans-serif'
  ctx.fillText('诺奖级 HP 滤波法 · ZSM-1.1 模型', M, M + 180)

  // 分数显示区
  const cardY = M + 230
  const cardW = W - 2 * M
  const cardH = 200
  ctx.fillStyle = '#ffffff'
  roundRect(ctx, M, cardY, cardW, cardH, 24)
  ctx.fill()
  ctx.fillStyle = '#6b7280'
  ctx.font = '400 22px -apple-system, "PingFang SC", sans-serif'
  ctx.fillText('孩子的中考成绩', M + 30, cardY + 50)

  if (blurScore) {
    // 贴纸感：模糊真分数打底 + 实体便签贴覆盖
    const scoreHidden = String(score).split('').join('  ') + '  分'
    drawStickerMask(
      ctx,
      M + 30,
      cardY + 75,
      cardW - 60,
      105,
      scoreHidden,
      '500 88px',
      '🔒  分数已隐藏',
      '点击上方按钮可解除',
      -1.8 // 旋转角度（度）
    )
  } else {
    ctx.fillStyle = '#111827'
    ctx.font = '300 100px -apple-system, "PingFang SC", sans-serif'
    const scoreText = String(score)
    const scoreTextWidth = ctx.measureText(scoreText).width
    ctx.fillText(scoreText, M + 30, cardY + 150)
    ctx.fillStyle = '#9ca3af'
    ctx.font = '400 30px -apple-system, "PingFang SC", sans-serif'
    ctx.fillText('分', M + 30 + scoreTextWidth + 10, cardY + 150)
  }

  // 估算名次区
  const rankCardY = cardY + 230
  const rankCardW = W - 2 * M
  const rankCardH = 130
  ctx.fillStyle = '#ffffff'
  roundRect(ctx, M, rankCardY, rankCardW, rankCardH, 24)
  ctx.fill()
  ctx.fillStyle = '#6b7280'
  ctx.font = '400 22px -apple-system, "PingFang SC", sans-serif'
  ctx.fillText('估算全市名次', M + 30, rankCardY + 50)

  if (blurScore) {
    const rankText = result.position.inTop300
      ? '前 300 名'
      : `约 ${result.position.estimatedRankRange.min} – ${result.position.estimatedRankRange.max} 名`
    drawStickerMask(
      ctx,
      M + 30,
      rankCardY + 65,
      rankCardW - 60,
      55,
      rankText,
      '500 36px',
      '🔒  名次已隐藏',
      null,
      1.5 // 反方向轻微倾斜，两张贴纸不平行更自然
    )
  } else {
    ctx.fillStyle = '#111827'
    ctx.font = '500 40px -apple-system, "PingFang SC", sans-serif'
    const rankText = result.position.inTop300
      ? '前 300 名'
      : `约 ${result.position.estimatedRankRange.min} – ${result.position.estimatedRankRange.max} 名`
    ctx.fillText(rankText, M + 30, rankCardY + 100)
  }

  // 五校结果（紧凑列表）
  const schoolsY = rankCardY + 150
  ctx.fillStyle = '#374151'
  ctx.font = '500 26px -apple-system, "PingFang SC", sans-serif'
  ctx.fillText(result.position.inTop300 ? '录取结果' : '五校录取概率', M, schoolsY + 26)

  let y = schoolsY + 56
  const CARD_H = 64
  const CARD_GAP = 8
  for (const s of result.schools.slice(0, 5)) {
    ctx.fillStyle = '#ffffff'
    roundRect(ctx, M, y, W - 2 * M, CARD_H, 14)
    ctx.fill()

    ctx.fillStyle = '#111827'
    ctx.font = '500 22px -apple-system, "PingFang SC", sans-serif'
    ctx.fillText(s.school, M + 20, y + 42)

    // 标签
    const tagText = tagLabelOf(s.tag)
    const tagColor = tagColorOf(s.tag)
    ctx.fillStyle = tagColor.bg
    const tagW = 70
    const tagH = 28
    roundRect(ctx, M + 190, y + 18, tagW, tagH, 14)
    ctx.fill()
    ctx.fillStyle = tagColor.text
    ctx.font = '500 16px -apple-system, "PingFang SC", sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText(tagText, M + 190 + tagW / 2, y + 37)
    ctx.textAlign = 'left'

    // 概率（右对齐）：可靠时（前300 / 结构确定 / 段内高位达「稳」）给数字，
    // 其余只给可能性描述词，对齐尾号经验分档、避免不可靠百分比
    const st = probStrength(s.point, s.lower, s.upper, s.structuralCertain)
    const showPercent = showProbPercent(s.school, s.tag, s.structuralCertain, result.position)
    ctx.fillStyle = '#111827'
    ctx.textAlign = 'right'
    if (showPercent) {
      ctx.font = '500 32px -apple-system, "PingFang SC", sans-serif'
      ctx.fillText(st.tone === 'none' ? '—' : `${st.display}%`, W - M - 20, y + 44)
    } else {
      ctx.font = '600 24px -apple-system, "PingFang SC", sans-serif'
      ctx.fillText(st.label, W - M - 20, y + 44)
    }
    ctx.textAlign = 'left'

    y += CARD_H + CARD_GAP
  }

  // 底部品牌 + 二维码占位
  const footerY = H - 220
  ctx.fillStyle = '#1e40af'
  roundRect(ctx, M, footerY, W - 2 * M, 160, 20)
  ctx.fill()
  ctx.fillStyle = '#ffffff'
  ctx.font = '600 26px -apple-system, "PingFang SC", sans-serif'
  ctx.fillText('扫码测算你家孩子的志愿', M + 24, footerY + 50)
  ctx.font = '400 18px -apple-system, "PingFang SC", sans-serif'
  ctx.fillText('方法星球 · 卓越教育出品', M + 24, footerY + 90)
  ctx.fillText('完全免费 · 数据本机计算', M + 24, footerY + 120)

  // 二维码（实图；若加载失败则降级为占位）
  const qrSize = 110
  const qrX = W - M - qrSize - 20
  const qrY = footerY + 25
  ctx.fillStyle = '#ffffff'
  roundRect(ctx, qrX, qrY, qrSize, qrSize, 10)
  ctx.fill()
  if (qrImage && qrImage.naturalWidth > 0) {
    ctx.save()
    roundRect(ctx, qrX + 4, qrY + 4, qrSize - 8, qrSize - 8, 6)
    ctx.clip()
    ctx.drawImage(qrImage, qrX + 4, qrY + 4, qrSize - 8, qrSize - 8)
    ctx.restore()
  } else {
    ctx.fillStyle = '#1e40af'
    ctx.font = '400 13px -apple-system, "PingFang SC", sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('二维码', qrX + qrSize / 2, qrY + qrSize / 2)
    ctx.fillText('（待替换）', qrX + qrSize / 2, qrY + qrSize / 2 + 18)
    ctx.textAlign = 'left'
  }
}

/**
 * 液态玻璃（Liquid Glass）遮罩 —— iOS 26 / macOS 26 设计语言
 *
 * 设计要点：
 *  1. 半透明蓝白渐变底（让背景"穿透"一点）
 *  2. 顶部高光带（specular highlight）— 仿光照
 *  3. 中央径向高光（让中部"亮"出来）
 *  4. 边缘亮线 + 外阴影（让玻璃"漂浮"在背景上）
 *  5. 圆角处的双层描边（亮线 + 微微暗影），制造厚度感
 *
 * 与传统毛玻璃的区别：
 *  - 毛玻璃：均匀磨砂感、纹理感
 *  - 液态玻璃：光泽感、有方向性的反光、有"水滴/水珠"般的湿润感
 */
/**
 * 液态玻璃 + 内容模糊遮蔽
 *
 * 视觉：玻璃下面"真的有数字"，但被强烈高斯模糊 + 液态玻璃光泽双重遮挡，
 *       看不清具体内容，但保留"这里被遮住了"的真实感。
 *
 * 技术：
 *  1. clip 到圆角区域
 *  2. 用 ctx.filter = 'blur(...)' 画模糊的真实数字
 *  3. 叠加液态玻璃的所有光泽层（背景、反光斑、折射晕、边缘亮线）
 *  4. 最后画中央信息（不模糊）
 *
 * 兼容性：ctx.filter 在 iOS Safari 9.1+ / Android Chrome / 微信 X5 全部支持。
 */
/**
 * 贴纸感遮挡
 *
 * 视觉：下层是真实数字的高斯模糊残影（"有内容，看不清"），
 *       上层是一张轻微倾斜的实体便签贴纸，带硬投影、紫色细边、右上撕角。
 *       像一张便利贴贴在了真实成绩单上，亲切、专业、隐私感强。
 *
 * 实现要点：
 *  - 下层模糊数字用 offscreen canvas + ctx.filter blur 绘制
 *  - 贴纸用 ctx.translate + rotate 实现倾斜，硬投影用 shadowOffsetY/shadowBlur
 *  - 撕角用三角剪切 + 折痕高光
 */
function drawStickerMask(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  hiddenText: string,
  hiddenFont: string,
  mainText: string,
  subText: string | null,
  rotateDeg: number
) {
  // ── 1. 模糊的真实数字打底（透出"贴纸下方有内容"的感觉） ──
  // 用 offscreen canvas 画清晰文字，主 ctx 用 filter blur 绘制到目标区域
  const off = document.createElement('canvas')
  const pad = 120
  off.width = Math.ceil(w + pad * 2)
  off.height = Math.ceil(h + pad * 2)
  const offCtx = off.getContext('2d')!
  offCtx.fillStyle = '#1e1147'
  offCtx.font = `${hiddenFont} -apple-system, "PingFang SC", sans-serif`
  offCtx.textAlign = 'center'
  offCtx.textBaseline = 'middle'
  offCtx.fillText(hiddenText, off.width / 2, off.height / 2)

  ctx.save()
  // 限制模糊层只画在容器范围内（避免越界）
  ctx.beginPath()
  roundRect(ctx, x, y, w, h, 16)
  ctx.clip()
  const blurRadius = Math.max(6, Math.min(h * 0.12, 12))
  ctx.filter = `blur(${blurRadius}px)`
  ctx.drawImage(off, x + w / 2 - off.width / 2, y + h / 2 - off.height / 2)
  ctx.restore()

  // ── 2. 贴纸本体（旋转 + 硬投影 + 圆角白底 + 紫色细边） ──
  const cx = x + w / 2
  const cy = y + h / 2
  // 贴纸略小于容器，给倾斜留出余量
  const stickerW = w - 14
  const stickerH = h - 10
  const sx = -stickerW / 2
  const sy = -stickerH / 2
  const radius = 14

  ctx.save()
  ctx.translate(cx, cy)
  ctx.rotate((rotateDeg * Math.PI) / 180)

  // 贴纸硬投影（短而清，区别于液态玻璃的柔光）
  ctx.shadowColor = 'rgba(60, 40, 120, 0.28)'
  ctx.shadowBlur = 16
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 6

  // 贴纸底色（米白带极轻紫调，像便签纸）
  ctx.fillStyle = '#fcfcff'
  roundRect(ctx, sx, sy, stickerW, stickerH, radius)
  ctx.fill()

  // 关闭阴影，避免后续描边/纹理重复投影
  ctx.shadowColor = 'transparent'
  ctx.shadowBlur = 0
  ctx.shadowOffsetY = 0

  // ── 3. 贴纸内部极淡的斜向纸纹（米色斜条，增强"纸张"质感） ──
  ctx.save()
  ctx.beginPath()
  roundRect(ctx, sx, sy, stickerW, stickerH, radius)
  ctx.clip()
  ctx.strokeStyle = 'rgba(180, 170, 210, 0.06)'
  ctx.lineWidth = 1
  for (let i = -stickerH; i < stickerW + stickerH; i += 8) {
    ctx.beginPath()
    ctx.moveTo(sx + i, sy)
    ctx.lineTo(sx + i - stickerH, sy + stickerH)
    ctx.stroke()
  }
  ctx.restore()

  // ── 4. 顶部一抹极淡高光（让纸张有体积感） ──
  ctx.save()
  ctx.beginPath()
  roundRect(ctx, sx, sy, stickerW, stickerH, radius)
  ctx.clip()
  const topShine = ctx.createLinearGradient(0, sy, 0, sy + stickerH * 0.35)
  topShine.addColorStop(0, 'rgba(255, 255, 255, 0.7)')
  topShine.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = topShine
  ctx.fillRect(sx, sy, stickerW, stickerH * 0.35)
  ctx.restore()

  // ── 5. 紫色细边（品牌一致性） ──
  ctx.strokeStyle = 'rgba(139, 92, 246, 0.55)'
  ctx.lineWidth = 1.4
  roundRect(ctx, sx + 0.7, sy + 0.7, stickerW - 1.4, stickerH - 1.4, radius - 0.4)
  ctx.stroke()

  // 内部紧贴的虚边（贴纸印刷感）
  ctx.save()
  ctx.strokeStyle = 'rgba(139, 92, 246, 0.22)'
  ctx.lineWidth = 0.8
  ctx.setLineDash([3, 3])
  roundRect(ctx, sx + 5, sy + 5, stickerW - 10, stickerH - 10, radius - 5)
  ctx.stroke()
  ctx.setLineDash([])
  ctx.restore()

  // ── 6. 右上撕角（小三角折痕） ──
  const cornerSize = 18
  const cornerX = sx + stickerW - cornerSize
  const cornerY = sy
  // 折下来的小三角（颜色比贴纸底深一点）
  ctx.fillStyle = '#e9e6f5'
  ctx.beginPath()
  ctx.moveTo(cornerX, cornerY)
  ctx.lineTo(sx + stickerW, cornerY)
  ctx.lineTo(sx + stickerW, cornerY + cornerSize)
  ctx.closePath()
  ctx.fill()
  // 折痕（贴纸本体的右上角被"切掉"，露出下层模糊数字）
  // 这里不再清空，而是画一条对角折痕亮线模拟折角效果
  ctx.strokeStyle = 'rgba(139, 92, 246, 0.4)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(cornerX, cornerY)
  ctx.lineTo(sx + stickerW, cornerY + cornerSize)
  ctx.stroke()
  // 折角上的小高光
  ctx.fillStyle = 'rgba(255, 255, 255, 0.55)'
  ctx.beginPath()
  ctx.moveTo(cornerX + 2, cornerY + 1)
  ctx.lineTo(sx + stickerW - 1, cornerY + 1)
  ctx.lineTo(sx + stickerW - 1, cornerY + cornerSize - 2)
  ctx.closePath()
  ctx.fill()

  // ── 7. 中央文字 ──
  const ty = subText ? -11 : 0
  ctx.fillStyle = '#3b2c6b'
  ctx.font = '600 28px -apple-system, "PingFang SC", "Apple Color Emoji", sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(mainText, 0, ty)

  if (subText) {
    ctx.fillStyle = '#7c6da8'
    ctx.font = '400 15px -apple-system, "PingFang SC", sans-serif'
    ctx.fillText(subText, 0, ty + 30)
  }

  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'

  ctx.restore()
}

function drawBlurredContentGlass(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  hiddenText: string,
  hiddenFont: string, // 如 '500 96px'
  mainText: string,
  subText: string | null
) {
  const r = Math.min(h / 2.2, 32)

  // ── 1. 外阴影（浮起感） ──
  ctx.save()
  ctx.shadowColor = 'rgba(120, 100, 200, 0.22)'
  ctx.shadowBlur = 28
  ctx.shadowOffsetY = 10
  ctx.fillStyle = 'rgba(255, 255, 255, 0.001)'
  roundRect(ctx, x, y, w, h, r)
  ctx.fill()
  ctx.restore()

  // ── 2. 进入剪切区 ──
  ctx.save()
  ctx.beginPath()
  roundRect(ctx, x, y, w, h, r)
  ctx.clip()

  // ── 3. 模糊的真实内容（offscreen canvas 画清晰文字，主 ctx 用 filter blur 绘制） ──
  // 关键参数：模糊半径要刚好让"字形轮廓"残留，但不可辨认
  const off = document.createElement('canvas')
  const pad = 120
  off.width = Math.ceil(w + pad * 2)
  off.height = Math.ceil(h + pad * 2)
  const offCtx = off.getContext('2d')!
  offCtx.fillStyle = '#1e1147'
  offCtx.font = `${hiddenFont} -apple-system, "PingFang SC", sans-serif`
  offCtx.textAlign = 'center'
  offCtx.textBaseline = 'middle'
  // 文字水平居中，但稍微偏下，让"分数已隐藏"label 不要正好压住所有数字痕迹
  offCtx.fillText(hiddenText, off.width / 2, off.height / 2)

  // 用 blur filter 绘制到主 ctx
  ctx.save()
  const blurRadius = Math.max(6, Math.min(h * 0.12, 12))
  ctx.filter = `blur(${blurRadius}px)`
  ctx.drawImage(off, x + w / 2 - off.width / 2, y + h / 2 - off.height / 2)
  ctx.restore()

  // ── 4. 玻璃基底（很淡，让下层模糊文字可见） ──
  const bg = ctx.createLinearGradient(x, y, x, y + h)
  bg.addColorStop(0, 'rgba(255, 255, 255, 0.35)')
  bg.addColorStop(0.5, 'rgba(238, 232, 255, 0.22)')
  bg.addColorStop(1, 'rgba(220, 215, 250, 0.18)')
  ctx.fillStyle = bg
  ctx.fillRect(x, y, w, h)

  // ── 5. 顶部镜面光带（更淡） ──
  const topShine = ctx.createLinearGradient(x, y, x, y + h * 0.4)
  topShine.addColorStop(0, 'rgba(255, 255, 255, 0.5)')
  topShine.addColorStop(0.5, 'rgba(255, 255, 255, 0.1)')
  topShine.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = topShine
  ctx.fillRect(x, y, w, h * 0.4)

  // ── 6. 左上反光斑（更淡） ──
  const specMain = ctx.createRadialGradient(
    x + w * 0.22,
    y + h * 0.22,
    0,
    x + w * 0.22,
    y + h * 0.22,
    w * 0.42
  )
  specMain.addColorStop(0, 'rgba(255, 255, 255, 0.35)')
  specMain.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = specMain
  ctx.fillRect(x, y, w, h)

  // ── 7. 二次镜面小亮点 ──
  const specMini = ctx.createRadialGradient(
    x + w * 0.14,
    y + h * 0.32,
    0,
    x + w * 0.14,
    y + h * 0.32,
    w * 0.08
  )
  specMini.addColorStop(0, 'rgba(255, 255, 255, 0.95)')
  specMini.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = specMini
  ctx.fillRect(x, y, w, h)

  // ── 8. 右下折射晕 ──
  const refr = ctx.createRadialGradient(
    x + w * 0.85,
    y + h * 0.9,
    0,
    x + w * 0.85,
    y + h * 0.9,
    w * 0.55
  )
  refr.addColorStop(0, 'rgba(167, 139, 250, 0.25)')
  refr.addColorStop(1, 'rgba(167, 139, 250, 0)')
  ctx.fillStyle = refr
  ctx.fillRect(x, y, w, h)

  // ── 9. 顶部边缘湿光线 ──
  const wetTop = ctx.createLinearGradient(x, y + 1, x + w, y + 1)
  wetTop.addColorStop(0, 'rgba(255, 255, 255, 0)')
  wetTop.addColorStop(0.3, 'rgba(255, 255, 255, 1)')
  wetTop.addColorStop(0.7, 'rgba(255, 255, 255, 1)')
  wetTop.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = wetTop
  ctx.fillRect(x, y, w, 1.5)

  ctx.restore() // 退出剪切

  // ── 10. 双层描边 ──
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.lineWidth = 1.2
  roundRect(ctx, x + 0.6, y + 0.6, w - 1.2, h - 1.2, r - 0.4)
  ctx.stroke()
  ctx.strokeStyle = 'rgba(130, 110, 200, 0.22)'
  ctx.lineWidth = 0.8
  roundRect(ctx, x + 1.8, y + 1.8, w - 3.6, h - 3.6, r - 1.8)
  ctx.stroke()

  // ── 11. 中央信息文字（清晰，不模糊） ──
  const cx = x + w / 2
  const cy = subText ? y + h / 2 - 11 : y + h / 2
  // 微微的文字阴影，让中央 label 在玻璃上"立"起来
  ctx.save()
  ctx.shadowColor = 'rgba(255, 255, 255, 0.6)'
  ctx.shadowBlur = 4
  ctx.fillStyle = '#374151'
  ctx.font = '600 28px -apple-system, "PingFang SC", "Apple Color Emoji", sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(mainText, cx, cy)

  if (subText) {
    ctx.fillStyle = '#6b7280'
    ctx.font = '400 15px -apple-system, "PingFang SC", sans-serif'
    ctx.fillText(subText, cx, cy + 30)
  }
  ctx.restore()

  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
}

function drawLiquidGlassMask(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  mainText: string,
  subText: string | null
) {
  const r = Math.min(h / 2.2, 32) // 大圆角，更"水珠"感

  // ── 1. 外发光阴影（双层：远柔 + 近紧） ──
  ctx.save()
  ctx.shadowColor = 'rgba(120, 100, 200, 0.22)'
  ctx.shadowBlur = 28
  ctx.shadowOffsetY = 10
  ctx.fillStyle = 'rgba(255, 255, 255, 0.001)'
  roundRect(ctx, x, y, w, h, r)
  ctx.fill()
  ctx.restore()

  // ── 2. 玻璃本体（淡蓝紫白多段渐变，带透明度） ──
  ctx.save()
  ctx.beginPath()
  roundRect(ctx, x, y, w, h, r)
  ctx.clip()

  // 主体底色：从顶部白光 → 中段淡紫透明 → 底部蓝紫
  const bodyGrad = ctx.createLinearGradient(x, y, x, y + h)
  bodyGrad.addColorStop(0, 'rgba(255, 255, 255, 0.85)')
  bodyGrad.addColorStop(0.4, 'rgba(238, 232, 255, 0.7)')
  bodyGrad.addColorStop(0.75, 'rgba(220, 215, 250, 0.62)')
  bodyGrad.addColorStop(1, 'rgba(196, 188, 240, 0.55)')
  ctx.fillStyle = bodyGrad
  ctx.fillRect(x, y, w, h)

  // ── 3. 顶部光泽带（specular highlight，类似镜面反光） ──
  const topShine = ctx.createLinearGradient(x, y, x, y + h * 0.5)
  topShine.addColorStop(0, 'rgba(255, 255, 255, 0.95)')
  topShine.addColorStop(0.5, 'rgba(255, 255, 255, 0.25)')
  topShine.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = topShine
  ctx.fillRect(x, y, w, h * 0.55)

  // ── 4. 左上大反光斑（主要高光，仿水珠的镜面亮点） ──
  const specMain = ctx.createRadialGradient(
    x + w * 0.22,
    y + h * 0.22,
    0,
    x + w * 0.22,
    y + h * 0.22,
    w * 0.42
  )
  specMain.addColorStop(0, 'rgba(255, 255, 255, 0.75)')
  specMain.addColorStop(0.6, 'rgba(255, 255, 255, 0.1)')
  specMain.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = specMain
  ctx.fillRect(x, y, w, h)

  // ── 5. 二次反光斑（小亮点，增强玻璃的"湿润"感） ──
  const specMini = ctx.createRadialGradient(
    x + w * 0.14,
    y + h * 0.32,
    0,
    x + w * 0.14,
    y + h * 0.32,
    w * 0.08
  )
  specMini.addColorStop(0, 'rgba(255, 255, 255, 0.95)')
  specMini.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = specMini
  ctx.fillRect(x, y, w, h)

  // ── 6. 右下色彩折射晕（让玻璃看起来在"过滤"颜色） ──
  const refr1 = ctx.createRadialGradient(
    x + w * 0.85,
    y + h * 0.9,
    0,
    x + w * 0.85,
    y + h * 0.9,
    w * 0.55
  )
  refr1.addColorStop(0, 'rgba(167, 139, 250, 0.28)')
  refr1.addColorStop(0.6, 'rgba(196, 180, 250, 0.1)')
  refr1.addColorStop(1, 'rgba(167, 139, 250, 0)')
  ctx.fillStyle = refr1
  ctx.fillRect(x, y, w, h)

  // ── 7. 顶部边缘"湿光"线（拉一条横向高亮带，仿玻璃顶部一抹反光） ──
  const wetTop = ctx.createLinearGradient(x, y + 1, x + w, y + 1)
  wetTop.addColorStop(0, 'rgba(255, 255, 255, 0)')
  wetTop.addColorStop(0.3, 'rgba(255, 255, 255, 1)')
  wetTop.addColorStop(0.7, 'rgba(255, 255, 255, 1)')
  wetTop.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = wetTop
  ctx.fillRect(x, y, w, 1.5)

  ctx.restore()

  // ── 8. 双层描边（创造立体厚度） ──
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.lineWidth = 1.2
  roundRect(ctx, x + 0.6, y + 0.6, w - 1.2, h - 1.2, r - 0.4)
  ctx.stroke()
  ctx.strokeStyle = 'rgba(130, 110, 200, 0.22)'
  ctx.lineWidth = 0.8
  roundRect(ctx, x + 1.8, y + 1.8, w - 3.6, h - 3.6, r - 1.8)
  ctx.stroke()

  // ── 9. 中央信息文字 ──
  const cx = x + w / 2
  const cy = subText ? y + h / 2 - 11 : y + h / 2
  ctx.fillStyle = '#374151'
  ctx.font = '600 28px -apple-system, "PingFang SC", "Apple Color Emoji", sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(mainText, cx, cy)

  if (subText) {
    ctx.fillStyle = '#6b7280'
    ctx.font = '400 15px -apple-system, "PingFang SC", sans-serif'
    ctx.fillText(subText, cx, cy + 30)
  }

  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function tagLabelOf(t: string): string {
  return ({ safe: '稳', recommended: '可报', reach: '冲', risky: '风险', unlikely: '低概率' } as any)[t] ?? t
}
function tagColorOf(t: string): { bg: string; text: string } {
  return (
    ({
      safe: { bg: '#d1fae5', text: '#065f46' },
      recommended: { bg: '#ddd6fe', text: '#5b21b6' },
      reach: { bg: '#fef3c7', text: '#92400e' },
      risky: { bg: '#fed7aa', text: '#9a3412' },
      unlikely: { bg: '#fee2e2', text: '#991b1b' }
    } as any)[t] ?? { bg: '#f3f4f6', text: '#374151' }
  )
}
